import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import StatusBadge from './StatusBadge';
import PriorityBadge from './PriorityBadge';
import { changeTaskStatus, getComments, createComment, getTaskHistory, updateChecklist, getAttachments, uploadAttachment, deleteAttachment, getWeeklyUpdates, upsertWeeklyUpdate } from '../utils/api';
import { formatDate, formatDateTime } from '../utils/dateHelpers';

// ─── Sección colapsable para el tab "Más" ─────────────────────────────────────
const Section = ({ title, icon, badge, defaultOpen = false, children, onOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => {
    if (!open && onOpen) onOpen();
    setOpen(o => !o);
  };
  return (
    <div className="rounded-xl overflow-hidden border border-border">
      <button onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-secondary/30 transition-colors"
        style={{ minHeight: 52 }}>
        <div className="flex items-center gap-2.5">
          <span className="text-lg">{icon}</span>
          <span className="text-sm font-semibold text-foreground">{title}</span>
          {badge > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary">{badge}</span>
          )}
        </div>
        <svg className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-border">
          {children}
        </div>
      )}
    </div>
  );
};

const TaskDrawer = ({ task, users = [], onClose, onUpdate }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [weeklyUpdates, setWeeklyUpdates] = useState([]);
  const [weeklyForm, setWeeklyForm] = useState({ titulo: '', seguimiento: '', cumplimiento_pct: 0, bloqueo: '', apoyo_requerido: '' });
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [localChecklist, setLocalChecklist] = useState(task.checklist || []);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [blockedReason, setBlockedReason] = useState('');
  const [weeklyStatus, setWeeklyStatus] = useState(null); // 'saved' | null

  const responsible = users.find(u => u.id === task.responsible_user_id);
  const creator = users.find(u => u.id === task.creator_id);
  const collaborators = users.filter(u => task.collaborator_ids?.includes(u.id));

  const isResponsible = task.responsible_user_id === user?.id;
  const canChangeStatus = user?.role === 'Presidencia' || user?.role === 'Gerencia' || isResponsible;
  const canEditChecklist = user?.role === 'Presidencia' || isResponsible;
  const canUploadAttachment = user?.role === 'Presidencia' || task.responsible_user_id === user?.id || task.collaborator_ids?.includes(user?.id);

  useEffect(() => {
    if (activeTab === 'seguimiento') loadWeeklyUpdates();
  }, [activeTab]);

  const loadWeeklyUpdates = async () => {
    try {
      const res = await getWeeklyUpdates(task.id);
      const updates = res.data || [];
      setWeeklyUpdates(updates);
      if (updates.length > 0) {
        const last = updates[0];
        setWeeklyForm({ titulo: '', seguimiento: '', cumplimiento_pct: last.cumplimiento_pct ?? 0, bloqueo: '', apoyo_requerido: '' });
      }
    } catch {}
  };

  const submitWeeklyUpdate = async () => {
    if (!weeklyForm.seguimiento.trim()) return;
    setWeeklyLoading(true);
    try {
      await upsertWeeklyUpdate(task.id, weeklyForm);
      setWeeklyStatus('saved');
      setTimeout(() => setWeeklyStatus(null), 3000);
      await loadWeeklyUpdates();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar seguimiento');
    } finally {
      setWeeklyLoading(false);
    }
  };

  const loadComments    = async () => { try { const r = await getComments(task.id); setComments(r.data); } catch {} };
  const loadHistory     = async () => { try { const r = await getTaskHistory(task.id); setHistory(r.data); } catch {} };
  const loadAttachments = async () => { try { const r = await getAttachments(task.id); setAttachments(r.data); } catch {} };

  const STATUS_TRANSITIONS = {
    "Pendiente":                        ["En progreso", "Recurrente", "Bloqueada", "No realizada"],
    "En progreso":                      ["Lista para revisión", "Bloqueada", "En espera de cliente", "En espera de usuario/Presidencia", "Aprobada / Cerrada", "No realizada"],
    "Recurrente":                       ["Bloqueada", "No realizada", "Aprobada / Cerrada"],
    "Bloqueada":                        ["En progreso", "Recurrente", "Pendiente", "No realizada"],
    "En espera de cliente":             ["En progreso", "Lista para revisión", "No realizada"],
    "En espera de usuario/Presidencia": ["En progreso", "Lista para revisión", "No realizada"],
    "Lista para revisión":              ["Aprobada / Cerrada", "Devuelta / Requiere ajustes"],
    "Devuelta / Requiere ajustes":      ["En progreso", "Lista para revisión", "No realizada"],
    "Aprobada / Cerrada":               [],
    "No realizada":                     [],
  };

  const availableStatuses = (() => {
    if (user?.role === 'Presidencia') return Object.keys(STATUS_TRANSITIONS).filter(s => s !== task.status);
    if (user?.role === 'Gerencia' && !isResponsible) {
      return task.status === 'Lista para revisión' ? ['Aprobada / Cerrada', 'Devuelta / Requiere ajustes'] : [];
    }
    return STATUS_TRANSITIONS[task.status] || [];
  })();

  const handleStatusChange = async (newStatus) => {
    if (!canChangeStatus) return;
    if (newStatus === 'No realizada' || newStatus === 'Bloqueada') {
      setPendingStatus(newStatus); setCancellationReason(''); setBlockedReason(''); return;
    }
    await submitStatusChange(newStatus, {});
  };

  const submitStatusChange = async (newStatus, extra = {}) => {
    try {
      setLoading(true);
      await changeTaskStatus(task.id, { status: newStatus, ...extra });
      setPendingStatus(null); onUpdate(); onClose();
    } catch (error) {
      alert('Error al cambiar el estado: ' + (error.response?.data?.detail || 'Error desconocido'));
    } finally { setLoading(false); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try { await createComment(task.id, newComment); setNewComment(''); loadComments(); } catch {}
  };

  const handleChecklistToggle = async (itemId) => {
    if (!canEditChecklist) return;
    const updated = localChecklist.map(i => i.id === itemId ? { ...i, completed: !i.completed } : i);
    setLocalChecklist(updated);
    try { await updateChecklist(task.id, updated); onUpdate(); } catch { setLocalChecklist(task.checklist); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > 20 * 1024 * 1024) { alert('El archivo es demasiado grande. Máximo 20MB'); return; }
    try {
      setUploading(true);
      await uploadAttachment(task.id, file); loadAttachments(); onUpdate(); e.target.value = '';
    } catch { alert('Error al subir archivo'); } finally { setUploading(false); }
  };

  const handleDeleteAttachment = async (attachmentId, uploadedBy) => {
    if (user?.role !== 'Presidencia' && uploadedBy !== user?.id) { alert('Solo puedes eliminar tus propios adjuntos'); return; }
    if (!window.confirm('¿Eliminar este adjunto?')) return;
    try { await deleteAttachment(task.id, attachmentId); loadAttachments(); onUpdate(); } catch { alert('Error al eliminar adjunto'); }
  };

  const getFileIcon = (mt) => {
    if (mt.startsWith('image/')) return '🖼️';
    if (mt === 'application/pdf') return '📄';
    if (mt.includes('excel') || mt.includes('spreadsheet')) return '📊';
    if (mt.includes('word') || mt.includes('document')) return '📝';
    return '📎';
  };
  const fmtSize = (b) => b < 1024 ? b + ' B' : b < 1048576 ? (b/1024).toFixed(1) + ' KB' : (b/1048576).toFixed(1) + ' MB';

  const TABS = [
    { key: 'details',    label: 'Detalles' },
    { key: 'seguimiento', label: 'Seguimiento' },
    { key: 'mas',        label: 'Más' },
  ];

  const completedChecklist = localChecklist.filter(i => i.completed).length;
  const pctColor = (p) => p >= 80 ? '#10B981' : p >= 50 ? '#F59E0B' : '#EF4444';
  const PCT_STEPS = [
    { val: 0, icon: '○' }, { val: 25, icon: '◔' }, { val: 50, icon: '◑' },
    { val: 75, icon: '◕' }, { val: 100, icon: '●' },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      <div className="fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-1/2 bg-background border-l border-border z-50 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-5 py-4 z-10">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0 mr-3">
              {task.responsabilidad && (
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#94A3B8' }}>
                  {task.responsabilidad}
                </p>
              )}
              <h2 className="text-lg font-bold text-foreground leading-snug mb-2">{task.title}</h2>
              <div className="flex flex-wrap gap-2">
                <StatusBadge status={task.status} />
                <PriorityBadge priority={task.priority} />
                {task.is_recurrent && (
                  <span className="chip-decko chip-inactive text-xs">🔁 Recurrente</span>
                )}
              </div>
            </div>
            <button onClick={onClose}
              className="flex-shrink-0 flex items-center justify-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              style={{ width: 40, height: 40 }}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 3 tabs */}
          <div className="flex gap-1">
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  activeTab === tab.key
                    ? 'bg-primary text-black'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}>
                {tab.label}
                {tab.key === 'mas' && (localChecklist.length + attachments.length + comments.length) > 0 && (
                  <span className="ml-1.5 text-xs opacity-70">
                    {localChecklist.length > 0 ? `· ${completedChecklist}/${localChecklist.length}` : ''}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-5 pb-20">

          {/* ── TAB: DETALLES ───────────────────────────────────────────── */}
          {activeTab === 'details' && (
            <div className="space-y-5">

              {/* Objetivo + Meta */}
              {(task.objetivo || task.meta) && (
                <div className="space-y-2.5">
                  {task.objetivo && (
                    <div className="rounded-xl p-3.5" style={{ background: 'rgba(0,208,132,0.06)', border: '1px solid rgba(0,208,132,0.15)' }}>
                      <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: '#00D084' }}>Objetivo</p>
                      <p className="text-sm text-foreground leading-relaxed">{task.objetivo}</p>
                    </div>
                  )}
                  {task.meta && (
                    <div className="rounded-xl p-3.5" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)' }}>
                      <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: '#6366F1' }}>Meta</p>
                      <p className="text-sm text-foreground leading-relaxed">{task.meta}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Descripción */}
              {task.description && (
                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">Descripción</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{task.description}</p>
                </div>
              )}

              {/* Cambiar Estado */}
              {canChangeStatus && availableStatuses.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Cambiar Estado</p>
                  <div className="flex flex-wrap gap-2">
                    {availableStatuses.map(s => (
                      <button key={s} onClick={() => handleStatusChange(s)} disabled={loading}
                        className={`text-sm px-4 py-2.5 rounded-xl font-semibold transition-colors border ${
                          s === 'No realizada'       ? 'border-red-300 text-red-600 bg-red-50 hover:bg-red-100' :
                          s === 'Bloqueada'           ? 'border-orange-300 text-orange-600 bg-orange-50 hover:bg-orange-100' :
                          s === 'Aprobada / Cerrada' ? 'border-green-300 text-green-600 bg-green-50 hover:bg-green-100' :
                          'border-border text-muted-foreground bg-secondary hover:bg-secondary/80'
                        }`}>
                        {s}
                      </button>
                    ))}
                  </div>

                  {pendingStatus === 'No realizada' && (
                    <div className="mt-3 p-4 rounded-xl border border-red-200 bg-red-50 space-y-3">
                      <p className="text-sm font-bold text-red-700">¿Por qué no se pudo realizar?</p>
                      <textarea rows={3} value={cancellationReason}
                        onChange={e => setCancellationReason(e.target.value)}
                        placeholder="Explica la razón..."
                        className="w-full rounded-xl px-4 py-3 border border-red-200 bg-white focus:outline-none resize-none"
                        style={{ fontSize: 16 }} />
                      <div className="flex gap-2">
                        <button onClick={() => submitStatusChange('No realizada', { cancellation_reason: cancellationReason })}
                          disabled={!cancellationReason.trim() || loading}
                          className="flex-1 text-sm py-3 rounded-xl font-bold bg-red-600 text-white disabled:opacity-40">Confirmar</button>
                        <button onClick={() => setPendingStatus(null)}
                          className="flex-1 text-sm py-3 rounded-xl font-bold bg-secondary text-muted-foreground">Cancelar</button>
                      </div>
                    </div>
                  )}

                  {pendingStatus === 'Bloqueada' && (
                    <div className="mt-3 p-4 rounded-xl border border-orange-200 bg-orange-50 space-y-3">
                      <p className="text-sm font-bold text-orange-700">¿Qué bloquea la tarea?</p>
                      <textarea rows={3} value={blockedReason}
                        onChange={e => setBlockedReason(e.target.value)}
                        placeholder="Describe el bloqueo..."
                        className="w-full rounded-xl px-4 py-3 border border-orange-200 bg-white focus:outline-none resize-none"
                        style={{ fontSize: 16 }} />
                      <div className="flex gap-2">
                        <button onClick={() => submitStatusChange('Bloqueada', { blocked_reason: blockedReason })}
                          disabled={!blockedReason.trim() || loading}
                          className="flex-1 text-sm py-3 rounded-xl font-bold bg-orange-600 text-white disabled:opacity-40">Confirmar</button>
                        <button onClick={() => setPendingStatus(null)}
                          className="flex-1 text-sm py-3 rounded-xl font-bold bg-secondary text-muted-foreground">Cancelar</button>
                      </div>
                    </div>
                  )}

                  {task.status === 'No realizada' && task.cancellation_reason && (
                    <div className="mt-2 p-3 rounded-xl bg-red-50 border border-red-100">
                      <p className="text-xs font-bold text-red-600 uppercase tracking-wide mb-1">Razón</p>
                      <p className="text-sm text-red-700">{task.cancellation_reason}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Equipo */}
              <div>
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Equipo</p>
                <div className="space-y-2">
                  {responsible && (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center text-primary font-bold flex-shrink-0">
                        {responsible.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{responsible.name}</p>
                        <p className="text-xs text-muted-foreground">Responsable</p>
                      </div>
                    </div>
                  )}
                  {collaborators.map(c => (
                    <div key={c.id} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center text-muted-foreground font-bold flex-shrink-0">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{c.name}</p>
                        <p className="text-xs text-muted-foreground">Colaborador</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fechas */}
              <div className="flex gap-4">
                {task.due_date && (
                  <div className="flex-1">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1">Fecha límite</p>
                    <p className="text-sm font-semibold text-foreground">{formatDate(task.due_date)}</p>
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1">Creada</p>
                  <p className="text-sm text-foreground">{formatDate(task.created_at)}</p>
                </div>
              </div>

              {creator && (
                <p className="text-xs text-muted-foreground">
                  Creada por <span className="font-semibold text-foreground">{creator.name}</span> ({task.created_by_role})
                </p>
              )}
            </div>
          )}

          {/* ── TAB: SEGUIMIENTO ────────────────────────────────────────── */}
          {activeTab === 'seguimiento' && (
            <div className="space-y-5">

              {task.responsible_user_id !== user?.id ? (
                <div className="rounded-xl p-4 bg-secondary/30 text-center">
                  <p className="text-sm text-muted-foreground">Solo el Responsable puede registrar el seguimiento semanal.</p>
                </div>
              ) : (
                <div className="space-y-4 rounded-xl p-4 border border-border">
                  <p className="text-sm font-semibold text-foreground">Esta semana</p>

                  {/* Título del avance */}
                  <div>
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">Título del avance</p>
                    <input type="text"
                      value={weeklyForm.titulo}
                      onChange={e => setWeeklyForm(f => ({ ...f, titulo: e.target.value }))}
                      placeholder="Ej: Cierre de propuesta con cliente, Entrega de reporte..."
                      className="w-full rounded-xl px-4 py-3 border border-border focus:outline-none"
                      style={{ fontSize: 16 }} />
                  </div>

                  {/* PCT selector */}
                  <div>
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Cumplimiento</p>
                    <div className="grid grid-cols-5 gap-1.5">
                      {PCT_STEPS.map(({ val, icon }) => {
                        const sel = weeklyForm.cumplimiento_pct === val;
                        const c = val === 0 ? '#94A3B8' : pctColor(val);
                        return (
                          <button key={val} type="button"
                            onClick={() => setWeeklyForm(f => ({ ...f, cumplimiento_pct: val }))}
                            className="rounded-xl text-center transition-all"
                            style={{
                              minHeight: 56,
                              background: sel ? c + '18' : 'rgba(0,0,0,0.03)',
                              border: `1.5px solid ${sel ? c : 'rgba(0,0,0,0.08)'}`,
                              transform: sel ? 'scale(1.04)' : 'scale(1)',
                            }}>
                            <div className="text-xl">{icon}</div>
                            <div className="text-xs font-bold mt-0.5" style={{ color: sel ? c : '#94A3B8' }}>{val}%</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Textarea */}
                  <div>
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">Seguimiento *</p>
                    <textarea rows={4}
                      value={weeklyForm.seguimiento}
                      onChange={e => setWeeklyForm(f => ({ ...f, seguimiento: e.target.value }))}
                      placeholder="¿Qué avanzó, qué pasó, qué decisiones se tomaron?"
                      className="w-full resize-none rounded-xl px-4 py-3 focus:outline-none border"
                      style={{ fontSize: 16, color: '#1E293B', background: '#F8FAFC', borderColor: weeklyForm.seguimiento.trim() ? 'rgba(0,200,120,0.35)' : undefined }} />
                  </div>

                  {/* Bloqueo + Apoyo */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">🚫 Bloqueo</p>
                      <input type="text" value={weeklyForm.bloqueo}
                        onChange={e => setWeeklyForm(f => ({ ...f, bloqueo: e.target.value }))}
                        placeholder="¿Qué frena el avance?"
                        className="w-full rounded-xl px-4 py-3 border border-border focus:outline-none"
                        style={{ fontSize: 16 }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">🙋 Apoyo</p>
                      <input type="text" value={weeklyForm.apoyo_requerido}
                        onChange={e => setWeeklyForm(f => ({ ...f, apoyo_requerido: e.target.value }))}
                        placeholder="¿Qué necesitas?"
                        className="w-full rounded-xl px-4 py-3 border border-border focus:outline-none"
                        style={{ fontSize: 16 }} />
                    </div>
                  </div>

                  <button onClick={submitWeeklyUpdate}
                    disabled={weeklyLoading || !weeklyForm.seguimiento.trim()}
                    className="w-full rounded-xl font-bold disabled:opacity-40 flex items-center justify-center gap-2"
                    style={{
                      height: 52, fontSize: 16,
                      background: !weeklyForm.seguimiento.trim() ? 'rgba(0,0,0,0.05)' : 'linear-gradient(135deg,#00D084,#00FFB3)',
                      color: !weeklyForm.seguimiento.trim() ? '#94A3B8' : '#000',
                    }}>
                    {weeklyLoading
                      ? <><div className="w-4 h-4 border-2 border-black/30 border-t-transparent rounded-full animate-spin" /> Guardando...</>
                      : weeklyStatus === 'saved'
                      ? '✓ ¡Guardado!'
                      : weeklyUpdates.length > 0 ? 'Actualizar seguimiento' : 'Guardar seguimiento'
                    }
                  </button>
                </div>
              )}

              {/* Historial de seguimientos */}
              {weeklyUpdates.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-3">
                    Historial · {weeklyUpdates.length} semana{weeklyUpdates.length !== 1 ? 's' : ''}
                  </p>
                  <div className="space-y-3">
                    {weeklyUpdates.map((upd, i) => {
                      const pct = upd.cumplimiento_pct ?? 0;
                      const pc = pctColor(pct);
                      const weekLabel = upd.week_start
                        ? new Date(upd.week_start).toLocaleDateString('es', { day: 'numeric', month: 'short' })
                        : '—';
                      return (
                        <div key={upd.id || i} className="rounded-xl border border-border overflow-hidden">
                          {/* Header de la semana */}
                          <div className="flex items-center justify-between px-4 py-2.5"
                            style={{ background: pc + '0D', borderBottom: `1.5px solid ${pc}25` }}>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold uppercase tracking-wide" style={{ color: pc }}>
                                Sem. {weekLabel}
                              </span>
                              {upd.titulo && (
                                <span className="text-xs font-semibold text-foreground">— {upd.titulo}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="rounded-full overflow-hidden" style={{ width: 48, height: 4, background: 'rgba(0,0,0,0.08)' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: pc, borderRadius: 9999 }} />
                              </div>
                              <span className="text-sm font-black tabular-nums" style={{ color: pc, minWidth: 34 }}>{pct}%</span>
                            </div>
                          </div>
                          {/* Cuerpo */}
                          <div className="px-4 py-3 space-y-2">
                            <p className="text-sm text-foreground leading-relaxed">{upd.seguimiento || upd.resultado || '—'}</p>
                            {(upd.bloqueo || upd.apoyo_requerido) && (
                              <div className="flex gap-2 flex-wrap pt-1">
                                {upd.bloqueo && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">🚫 {upd.bloqueo}</span>
                                )}
                                {upd.apoyo_requerido && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-warning/10 text-warning">🙋 {upd.apoyo_requerido}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── TAB: MÁS ────────────────────────────────────────────────── */}
          {activeTab === 'mas' && (
            <div className="space-y-3">

              {/* Checklist */}
              <Section title="Checklist" icon="✅"
                badge={localChecklist.length > 0 ? completedChecklist : 0}
                defaultOpen={localChecklist.length > 0}>
                {localChecklist.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-3 text-center">No hay checklist en esta tarea.</p>
                ) : (
                  <div className="space-y-2 pt-2">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-1 rounded-full overflow-hidden" style={{ height: 4, background: 'rgba(0,0,0,0.07)' }}>
                        <div style={{ width: `${(completedChecklist / localChecklist.length) * 100}%`, height: '100%', background: '#10B981', borderRadius: 9999, transition: 'width 0.4s' }} />
                      </div>
                      <span className="text-xs font-bold text-foreground">{completedChecklist}/{localChecklist.length}</span>
                    </div>
                    {localChecklist.map(item => (
                      <label key={item.id}
                        className="flex items-center gap-3 p-3 rounded-xl border border-border cursor-pointer hover:bg-secondary/20 transition-colors">
                        <input type="checkbox" checked={item.completed}
                          onChange={() => handleChecklistToggle(item.id)}
                          disabled={!canEditChecklist}
                          className="w-5 h-5 rounded flex-shrink-0"
                          style={{ accentColor: '#00C878' }} />
                        <span className={`flex-1 text-sm ${item.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                          {item.text}
                        </span>
                      </label>
                    ))}
                    {!canEditChecklist && (
                      <p className="text-xs text-muted-foreground text-center pt-1">Solo el responsable puede editar el checklist.</p>
                    )}
                  </div>
                )}
              </Section>

              {/* Adjuntos */}
              <Section title="Adjuntos" icon="📎" badge={attachments.length} onOpen={loadAttachments}>
                {canUploadAttachment && (
                  <div className="pt-2 pb-3">
                    <input type="file" onChange={handleFileUpload} disabled={uploading}
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                      className="w-full rounded-xl border border-border px-3 py-2.5 text-sm bg-background disabled:opacity-50" />
                    {uploading && <p className="text-xs text-muted-foreground mt-1.5">Subiendo archivo...</p>}
                  </div>
                )}
                {attachments.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-3 text-center">Sin adjuntos.</p>
                ) : (
                  <div className="space-y-2 pt-2">
                    {attachments.map(a => {
                      const uploader = users.find(u => u.id === a.uploaded_by);
                      return (
                        <div key={a.id} className="rounded-xl border border-border p-3">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl flex-shrink-0">{getFileIcon(a.mime_type)}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{a.filename}</p>
                              <p className="text-xs text-muted-foreground">{fmtSize(a.size)} · {uploader?.name || 'Usuario'}</p>
                            </div>
                          </div>
                          {a.mime_type.startsWith('image/') && (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${task.id}/attachments/${a.id}/download`}
                              alt={a.filename} className="mt-2 rounded-lg max-h-48 w-full object-contain border border-border" />
                          )}
                          <div className="flex gap-2 mt-2.5">
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${task.id}/attachments/${a.id}/download`}
                              target="_blank" rel="noopener noreferrer"
                              className="flex-1 text-center text-sm py-2 rounded-xl bg-primary/10 text-primary font-semibold">
                              Descargar
                            </a>
                            {(user?.role === 'Presidencia' || a.uploaded_by === user?.id) && (
                              <button onClick={() => handleDeleteAttachment(a.id, a.uploaded_by)}
                                className="px-4 text-sm py-2 rounded-xl bg-destructive/10 text-destructive font-semibold">
                                Eliminar
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>

              {/* Comentarios */}
              <Section title="Comentarios" icon="💬" badge={comments.length} onOpen={loadComments}>
                <div className="pt-2 space-y-3">
                  <div className="space-y-2">
                    <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                      placeholder="Escribe un comentario..."
                      rows={3} className="w-full rounded-xl border border-border px-4 py-3 focus:outline-none resize-none text-sm bg-background"
                      style={{ fontSize: 16 }} />
                    <button onClick={handleAddComment} disabled={!newComment.trim()}
                      className="w-full py-2.5 rounded-xl text-sm font-bold bg-primary text-black disabled:opacity-40">
                      Publicar
                    </button>
                  </div>
                  {comments.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-3 text-center">Sin comentarios aún.</p>
                  ) : (
                    comments.map(comment => {
                      const author = users.find(u => u.id === comment.user_id);
                      return (
                        <div key={comment.id} className="rounded-xl border border-border p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                              {author?.name.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">{author?.name || 'Usuario'}</p>
                              <p className="text-xs text-muted-foreground">{formatDateTime(comment.created_at)}</p>
                            </div>
                          </div>
                          <p className="text-sm text-foreground">{comment.text}</p>
                        </div>
                      );
                    })
                  )}
                </div>
              </Section>

              {/* Historial de cambios */}
              <Section title="Historial de cambios" icon="🕐" onOpen={loadHistory}>
                {history.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-3 text-center">Sin historial.</p>
                ) : (
                  <div className="space-y-3 pt-2">
                    {history.map(record => {
                      const actor = users.find(u => u.id === record.user_id);
                      return (
                        <div key={record.id} className="flex gap-3">
                          <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {actor?.name.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0 pt-0.5">
                            <p className="text-sm text-foreground">
                              <span className="font-semibold">{actor?.name || 'Usuario'}</span>{' '}
                              {record.action === 'created'           && 'creó la tarea'}
                              {record.action === 'status_changed'    && `cambió el estado → "${record.new_value}"`}
                              {record.action === 'updated'           && 'actualizó la tarea'}
                              {record.action === 'collaborator_added'&& 'agregó un colaborador'}
                              {record.action === 'comment_added'     && 'agregó un comentario'}
                              {record.action === 'checklist_updated' && 'actualizó el checklist'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">{formatDateTime(record.timestamp)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>

            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default TaskDrawer;
